function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import capitalize from 'lodash/capitalize';
import isString from 'lodash/isString';
import pickBy from 'lodash/pickBy';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import formControlState from '@material-ui/core/FormControl/formControlState';
import { withStyles } from '@material-ui/core/styles';
import renderIf from '@signal/utils/renderIf';
import FormLabel from '../FormLabel';
import { ControlWrapper, LabelWrapper, LeftAdornmentWrapper, ErrorLabel, styles } from './styles';
export var FormControlLabel = /*#__PURE__*/memo(function (props) {
  var _classNames;

  var classes = props.classes,
      classNameProp = props.className,
      control = props.control,
      inputRef = props.inputRef,
      label = props.label,
      labelPlacement = props.labelPlacement,
      leftAdornment = props.leftAdornment,
      name = props.name,
      onChange = props.onChange,
      value = props.value,
      error = props.error,
      other = _objectWithoutProperties(props, ["classes", "className", "control", "inputRef", "label", "labelPlacement", "leftAdornment", "name", "onChange", "value", "error"]);

  var hasErrorMessage = isString(error);
  var hasError = hasErrorMessage ? Boolean(error) : error;
  var stateKeys = ['required', 'focused', 'disabled', 'error'];
  var controlPropKeys = stateKeys.concat(['checked', 'name', 'onChange', 'value', 'inputRef']);
  var controlProps = pickBy(formControlState({
    props: _objectSpread(_objectSpread({}, props), {}, {
      error: hasError
    }),
    muiFormControl: control.props,
    states: controlPropKeys
  }), Boolean);
  var labelProps = pick(controlProps, stateKeys);
  return /*#__PURE__*/React.createElement("label", {
    className: classNames(classes.root, (_classNames = {}, _defineProperty(_classNames, classes["labelPlacement".concat(capitalize(labelPlacement))], labelPlacement !== 'end'), _defineProperty(_classNames, classes.disabled, controlProps.disabled), _classNames), classNameProp)
  }, /*#__PURE__*/React.createElement(ControlWrapper, null, /*#__PURE__*/React.cloneElement(control, controlProps)), renderIf(leftAdornment, function () {
    return /*#__PURE__*/React.createElement(LeftAdornmentWrapper, null, leftAdornment);
  }), /*#__PURE__*/React.createElement(LabelWrapper, null, renderIf(isString(label), function () {
    return /*#__PURE__*/React.createElement(FormLabel, Object.assign({
      component: "span"
    }, labelProps, omit(other, controlPropKeys)), label);
  }, function () {
    return label;
  })), renderIf(hasErrorMessage, function () {
    return /*#__PURE__*/React.createElement(ErrorLabel, {
      variant: "caption",
      component: "span",
      error: true
    }, error);
  }));
});
FormControlLabel.propTypes = {
  /**
   * If `true`, the component appears selected.
   */
  checked: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  /**
   * Override or extend the styles applied to the component.
   * See [CSS API](#css-api) below for more details.
   */
  classes: PropTypes.object.isRequired,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * A control element. For instance, it can be be a `Radio`, a `Switch` or a `Checkbox`.
   */
  control: PropTypes.element,

  /**
   * If `true`, the control will be disabled.
   */
  disabled: PropTypes.bool,

  /**
   * Use that property to pass a ref callback to the native input component.
   */
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),

  /**
   * The text to be used in an enclosing label element.
   */
  label: PropTypes.node,

  /**
   * The position of the label.
   */
  labelPlacement: PropTypes.oneOf(['end', 'start', 'top', 'bottom']),

  /**
   * If `true`, the label should be displayed in an error state.
   */
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),

  /**
   * If `true`, the input of this label is focused (used by `FormGroup` components).
   */
  focused: PropTypes.bool,

  /**
   * @ignore
   */
  muiFormControl: PropTypes.object,

  /**
   * If `true`, the label will indicate that the input is required.
   */
  required: PropTypes.bool,

  /*
   * @ignore
   */
  name: PropTypes.string,

  /**
   * Callback fired when the state is changed.
   *
   * @param {object} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.checked`.
   * @param {boolean} checked The `checked` value of the switch
   */
  onChange: PropTypes.func,

  /**
   * The value of the component.
   */
  value: PropTypes.string
};
FormControlLabel.defaultProps = {
  labelPlacement: 'top'
};
export default withStyles(styles, {
  name: 'FormControlLabel'
})(FormControlLabel);